/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * featureOverrideStorage — DEBUG-ONLY persistence for Feature Overrides.
 *
 * Synchronous `localStorage` (mirroring `appVersionOverride`/`mockOverride`),
 * because the Feature Activation read path (`isFeatureEnabled`) is synchronous
 * and cannot await IndexedDB. `FeatureActivation` never touches this module
 * directly — reads reach it through `FeatureOverrideProvider`, writes through
 * the Feature Override ViewModel.
 *
 * Everything here is gated by `isDevToolsBuild()`: in a release build every
 * entry point is inert (reads return `null`/`{}`, writes are no-ops), so no
 * override can exist and the debug screen that drives writes is unreachable.
 *
 * Storage shape under the single key `featureActivation.overrides`:
 *   { "LIVETV": true, "MYHUB": false }
 * Only keys matching a registered `Feature` are honoured on read, so a
 * hand-edited or stale unknown key can never enable arbitrary functionality.
 */

import { registeredFeatures } from "../../model/featureActivation";
import type { Feature } from "../../model/featureActivation";
import { Log } from "../../model/logging/Log";
import { isDevToolsBuild } from "../appVersionOverride";

const TAG: string = "featureOverrideStorage";

/** Namespaced key holding the developer/QE override map. */
export const FEATURE_OVERRIDE_STORAGE_KEY: string =
  "featureActivation.overrides";

const KNOWN_FEATURES: ReadonlySet<string> = new Set<string>(registeredFeatures);

function hasBrowserStorage(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

/**
 * The full override map, restricted to registered features. Returns an empty
 * map in a release build, when storage is unavailable, or when the stored
 * value is missing or malformed — a corrupt value is treated as absent rather
 * than crashing the app.
 */
export function readFeatureOverrides(): Record<string, boolean> {
  if (!isDevToolsBuild() || !hasBrowserStorage()) {
    return {};
  }
  try {
    const raw: string | null = window.localStorage.getItem(
      FEATURE_OVERRIDE_STORAGE_KEY
    );
    if (raw === null) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return {};
    }
    const source = parsed as Record<string, unknown>;
    const result: Record<string, boolean> = {};
    const keys: Array<string> = Object.keys(source);
    for (let i = 0; i < keys.length; i += 1) {
      const key: string = keys[i];
      const value: unknown = source[key];
      if (KNOWN_FEATURES.has(key) && typeof value === "boolean") {
        result[key] = value;
      }
    }
    return result;
  } catch (error: unknown) {
    Log.w(TAG, "readFeatureOverrides() — unreadable override store", error);
    return {};
  }
}

function writeFeatureOverrides(overrides: Record<string, boolean>): void {
  if (!isDevToolsBuild() || !hasBrowserStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(
      FEATURE_OVERRIDE_STORAGE_KEY,
      JSON.stringify(overrides)
    );
  } catch (error: unknown) {
    Log.w(TAG, "writeFeatureOverrides() — could not persist overrides", error);
  }
}

/** The override for one feature, or `null` when none is set. */
export function getFeatureOverride(feature: Feature): boolean | null {
  const overrides: Record<string, boolean> = readFeatureOverrides();
  if (Object.prototype.hasOwnProperty.call(overrides, feature)) {
    return overrides[feature];
  }
  return null;
}

/** Forces a feature ON (`true`) or OFF (`false`). */
export function setFeatureOverride(feature: Feature, value: boolean): void {
  const overrides: Record<string, boolean> = readFeatureOverrides();
  overrides[feature] = value;
  writeFeatureOverrides(overrides);
}

/** Removes a single override so normal activation resumes (DEFAULT). */
export function clearFeatureOverride(feature: Feature): void {
  const overrides: Record<string, boolean> = readFeatureOverrides();
  if (Object.prototype.hasOwnProperty.call(overrides, feature)) {
    delete overrides[feature];
    writeFeatureOverrides(overrides);
  }
}

/** Clears every override (Reset All). */
export function clearAllFeatureOverrides(): void {
  if (!isDevToolsBuild() || !hasBrowserStorage()) {
    return;
  }
  try {
    window.localStorage.removeItem(FEATURE_OVERRIDE_STORAGE_KEY);
  } catch (error: unknown) {
    Log.w(TAG, "clearAllFeatureOverrides() — could not clear overrides", error);
  }
}
